import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import './Ecosystem.css';

// 生态展示视频。新增视频：把 mp4 放到 server/uploads/videos/ 后，在下方数组加一行即可
const ECO_VIDEOS = [
  '/uploads/videos/video-eco-1.mp4',
  '/uploads/videos/video-eco-2.mp4',
  '/uploads/videos/video-eco-3.mp4',
  '/uploads/videos/video-eco-4.mp4',
];

const Ecosystem: React.FC = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="ecosystem-page">
      <section className="eco-hero">
        <div className="hero-content">
          <div className="hero-badge">{t.eco.badge}</div>
          <h1 className="hero-title">
            <span className="gradient-text">{t.eco.hero_title1}</span>{' '}
            <span>{t.eco.hero_title2}</span>
          </h1>
          <p className="hero-subtitle">{t.eco.hero_subtitle}</p>
        </div>
      </section>

      <div className="eco-content">
        <div className="section-block">
          <div className="section-label">{t.eco.overview_label}</div>
          <h2>
            <span>{t.eco.overview_title1}</span>{' '}
            <span className="gradient-text">{t.eco.overview_title2}</span>
          </h2>
          <p>{t.eco.overview_desc}</p>
        </div>

        <div className="note">
          <strong>{t.eco.note_label}</strong>
          <span>{t.eco.note_desc}</span>
        </div>

        <div className="divider"></div>

        <div className="section-block">
          <div className="section-label">{t.eco.part1_label}</div>
          <h2>
            <span>{t.eco.part1_title1}</span>{' '}
            <span className="gradient-text">{t.eco.part1_title2}</span>
          </h2>
          <p>{t.eco.part1_desc}</p>
        </div>

        <div className="eco-grid">
          <div className="eco-card">
            <div className="eco-icon">◆</div>
            <h3>{t.eco.base_title}</h3>
            <p>{t.eco.base_desc1}</p>
            <p>{t.eco.base_desc2}</p>
          </div>
          <div className="eco-card">
            <div className="eco-icon">✦</div>
            <h3>{t.eco.agg_title}</h3>
            <p>{t.eco.agg_desc1}</p>
            <p>{t.eco.agg_desc2}</p>
          </div>
          <div className="eco-card">
            <div className="eco-icon">♦</div>
            <h3>{t.eco.fin_title}</h3>
            <p>{t.eco.fin_desc1}</p>
            <p>{t.eco.fin_desc2}</p>
          </div>
          <div className="eco-card">
            <div className="eco-icon">♥</div>
            <h3>{t.eco.ent_title}</h3>
            <p>{t.eco.ent_desc1}</p>
            <p>{t.eco.ent_desc2}</p>
          </div>
        </div>

        <div className="synergy-box">
          <h3>{t.eco.synergy_title}</h3>
          <p>{t.eco.synergy_desc}</p>
        </div>

        <div className="divider"></div>

        <div className="section-block">
          <div className="section-label">{t.eco.videos_label}</div>
          <h2>
            <span>{t.eco.videos_title1}</span>{' '}
            <span className="gradient-text">{t.eco.videos_title2}</span>
          </h2>
        </div>

        <EcoVideoSlider videos={ECO_VIDEOS} />

        <div className="divider"></div>

        <div className="section-block">
          <div className="section-label">{t.eco.part2_label}</div>
          <h2>
            <span>{t.eco.part2_title1}</span>
            <span className="gradient-text">{t.eco.part2_title2}</span>{' '}
            <span>{t.eco.part2_title3}</span>
          </h2>
          <p>{t.eco.part2_desc}</p>
        </div>

        <div className="layer-grid">
          <div className="layer-card">
            <h3>{t.eco.consensus_title}</h3>
            <p>{t.eco.consensus_desc1}</p>
            <p>{t.eco.consensus_desc2}</p>
          </div>
          <div className="layer-card">
            <h3>{t.eco.incentive_title}</h3>
            <p>{t.eco.incentive_desc1}</p>
            <p>{t.eco.incentive_desc2}</p>
          </div>
          <div className="layer-card">
            <h3>{t.eco.app_title}</h3>
            <p>{t.eco.app_desc1}</p>
            <p>{t.eco.app_desc2}</p>
            <p>{t.eco.app_desc3}</p>
          </div>
        </div>

        <div className="divider"></div>

        <div className="section-block">
          <div className="section-label">{t.eco.part3_label}</div>
          <h2>
            <span>{t.eco.part3_title1}</span>{' '}
            <span className="gradient-text">{t.eco.part3_title2}</span>
          </h2>
          <p>{t.eco.part3_desc2}</p>
        </div>

        <div className="closing">
          <p>{t.eco.closing_desc}</p>
        </div>
      </div>
    </div>
  );
};

/**
 * 生态视频滑动区
 * - 触屏 / 触控板：原生横向滚动 + CSS scroll-snap 吸附
 * - 鼠标：左右箭头按钮、底部圆点
 * - 同一时刻只播放一个视频，避免多路声音叠加
 */
const EcoVideoSlider: React.FC<{ videos: string[] }> = ({ videos }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // 依据滚动位置更新当前项与首尾状态（滚动过程中持续调用）
  const syncState = () => {
    const track = trackRef.current;
    if (!track) return;
    const cards = Array.from(track.querySelectorAll<HTMLElement>('.eco-video-card'));
    if (!cards.length) return;

    // 用 getBoundingClientRect 比较：相对视口左缘最近的卡片即当前项
    const trackLeft = track.getBoundingClientRect().left;
    let nearest = 0;
    let minDistance = Infinity;
    cards.forEach((card, i) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = i;
      }
    });
    setActive(nearest);

    // 留 2px 容差，避免亚像素误差导致按钮状态抖动
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 2);
    setAtEnd(track.scrollLeft >= maxScrollLeft - 2);
  };

  useEffect(() => {
    syncState();
    // 首屏渲染后与窗口尺寸变化时重新计算
    window.addEventListener('resize', syncState);
    return () => window.removeEventListener('resize', syncState);
  }, [videos.length]);

  // 滚动到指定视频（浏览器会自动夹取到可滚动范围）
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(videos.length - 1, index));
    const card = track.querySelectorAll<HTMLElement>('.eco-video-card')[clamped];
    if (!card) return;
    const delta = card.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollTo({ left: track.scrollLeft + delta, behavior: 'smooth' });
  };

  // 按一屏一张卡片滑动（用于箭头）
  const step = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>('.eco-video-card');
    if (!card) return;
    const style = window.getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
  };

  return (
    <div className="eco-video-slider">
      <div className="eco-video-viewport">
        <div className="eco-video-track" ref={trackRef} onScroll={syncState}>
          {videos.map((src) => (
            <div className="eco-video-card" key={src}>
              <video
                controls
                playsInline
                preload="metadata"
                src={src}
                onPlay={(e) => {
                  // 播放当前视频时暂停其余视频
                  trackRef.current?.querySelectorAll('video').forEach((v) => {
                    if (v !== e.currentTarget) v.pause();
                  });
                }}
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          className="eco-video-nav eco-video-nav-prev"
          aria-label="Previous video"
          onClick={() => step(-1)}
          disabled={atStart}
        >
          ‹
        </button>
        <button
          type="button"
          className="eco-video-nav eco-video-nav-next"
          aria-label="Next video"
          onClick={() => step(1)}
          disabled={atEnd}
        >
          ›
        </button>
      </div>

      <div className="eco-video-dots">
        {videos.map((src, i) => (
          <button
            type="button"
            key={src}
            className={`eco-video-dot ${i === active ? 'active' : ''}`}
            aria-label={`Video ${i + 1} of ${videos.length}`}
            aria-current={i === active}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </div>
  );
};

export default Ecosystem;
